import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Bot, Shield, Lock, Users, Building2,
  CheckCircle, Server, Key, FileCheck, Globe, Zap,
  BarChart3, Settings, Network, Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';

const EnterprisePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-700 to-cyan-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              Enterprise-Grade AI
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              AI Automation Built for Enterprise Scale
            </h1>
            <p className="text-xl md:text-2xl text-teal-100 max-w-2xl mx-auto mb-8">
              Governance. Security. Multi-team orchestration. Deploy AI across your organization with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:enterprise@harmonix.info?subject=Enterprise%20Demo%20Request">
                <Button size="lg" className="bg-white text-teal-800 hover:bg-slate-100 px-8">
                  Schedule Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <Link to="/docs/user-manual">
                <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 px-8">
                  View Documentation
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-slate-400">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">SOC 2 Type II</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              <span className="text-sm font-medium">256-bit Encryption</span>
            </div>
            <div className="flex items-center gap-2">
              <FileCheck className="w-5 h-5" />
              <span className="text-sm font-medium">GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5" />
              <span className="text-sm font-medium">99.99% Uptime SLA</span>
            </div>
          </div>
        </div>
      </section>

      {/* Governance Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Enterprise Governance & Control
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Maintain complete visibility and control over AI deployments across your organization.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, title: 'Role-Based Access', desc: 'Granular permissions for admins, managers, and agents' },
              { icon: Settings, title: 'Policy Enforcement', desc: 'Define and enforce AI behavior policies organization-wide' },
              { icon: BarChart3, title: 'Audit Logging', desc: 'Complete audit trail of all AI interactions and changes' },
              { icon: Key, title: 'SSO Integration', desc: 'SAML/OIDC single sign-on with your identity provider' }
            ].map((feature, idx) => (
              <Card key={feature.title} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <feature.icon className="w-10 h-10 text-slate-700 mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Security That Meets Your Standards
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                We've built Harmonix with security-first architecture. Your data is protected at every layer.
              </p>
              <ul className="space-y-4">
                {[
                  'End-to-end encryption for all data in transit and at rest',
                  'Private cloud deployment options available',
                  'Regular third-party penetration testing',
                  'Data residency options (US, EU, APAC)',
                  'Dedicated security team and incident response',
                  'Custom data retention and deletion policies'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-900 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-6">Security Certifications</h3>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: Shield, name: 'SOC 2 Type II', status: 'Certified' },
                  { icon: Lock, name: 'ISO 27001', status: 'Certified' },
                  { icon: FileCheck, name: 'GDPR', status: 'Compliant' },
                  { icon: Server, name: 'HIPAA', status: 'Available' }
                ].map((cert) => (
                  <div key={cert.name} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                      <cert.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium">{cert.name}</p>
                      <p className="text-sm text-slate-400">{cert.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Multi-Team Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Multi-Team Orchestration
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Deploy AI assistants across departments while maintaining centralized control.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Building2, title: 'Departmental Isolation', desc: 'Each team gets their own AI workspace with custom knowledge bases and behavior rules.' },
              { icon: Network, title: 'Centralized Management', desc: 'Admins can monitor, configure, and update all team deployments from one dashboard.' },
              { icon: Database, title: 'Shared Knowledge', desc: 'Create company-wide knowledge bases that all departments can access while maintaining team-specific info.' }
            ].map((feature) => (
              <Card key={feature.title} className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-slate-700" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Enterprise Integrations
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Connect Harmonix to your existing enterprise stack.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {['Salesforce', 'ServiceNow', 'Zendesk', 'Microsoft Teams', 'Slack', 'SAP', 'Oracle', 'Workday'].map((integration) => (
              <div key={integration} className="px-6 py-3 bg-white rounded-lg border border-slate-200 text-slate-700 font-medium">
                {integration}
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 mt-6 text-sm">
            Custom integrations available via REST API and webhooks
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Scale AI Across Your Organization?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Our enterprise team will work with you to design the perfect deployment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:enterprise@harmonix.info?subject=Enterprise%20Demo%20Request">
              <Button size="lg" className="bg-white text-teal-800 hover:bg-slate-100 px-8">
                Schedule Enterprise Demo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <a href="mailto:enterprise@harmonix.info?subject=Security%20Whitepaper%20Request">
              <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 px-8">
                Request Security Whitepaper
              </Button>
            </a>
          </div>
          <p className="text-teal-200 text-sm mt-6">
            Or email us at <a href="mailto:enterprise@harmonix.info" className="text-white hover:underline font-medium">enterprise@harmonix.info</a>
          </p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default EnterprisePage;
